import { ArrowDownTrayIcon, UserPlusIcon } from "@heroicons/react/16/solid";
import {
  ChatBubbleOvalLeftIcon as ChatIconOutline,
  HomeIcon as HomeIconOutline,
  PlusCircleIcon as PlusIconOutline,
  UserIcon as UserIconOutline
} from "@heroicons/react/24/outline";

import {
  ChatBubbleOvalLeftIcon,
  ChatBubbleOvalLeftIcon as ChatIconSolid,
  Cog6ToothIcon,
  Cog8ToothIcon,
  HomeIcon as HomeIconSolid,
  InboxArrowDownIcon,
  PencilIcon,
  PlusCircleIcon,
  PlusCircleIcon as PlusIconSolid,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon as UserIconSolid
} from "@heroicons/react/24/solid";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@moe/oss/ui/dropdown-menu";
import { LogoButton } from "@moe/oss/ui/logo-button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@moe/oss/ui/tooltip";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { OptionsModal } from "@web/components/OptionsModal";
import { CreateGroupChatModal } from "@web/route-services/index/CreateGroupChatModal";
import { FileRouteTypes } from "@web/routeTree.gen";
import { motion } from "motion/react";
import { FaAddressCard } from "react-icons/fa";
import { useAppContext } from "../route-services/root/AppContext";
import { AuthModal } from "./AuthModal";

export function Navbar() {
  const location = useLocation();

  const { createModal, session, isDesktopScreen } = useAppContext();
  const nav = useNavigate();

  const handleProtectedRoute = (to: FileRouteTypes["to"]) => {
    if (!session) {
      createModal({ content: <AuthModal formKind="signup" /> });
      return;
    }
    nav({ to });
  };

  const handleProfileRoute = async () => {
    if (!session) {
      createModal({ content: <AuthModal formKind="signup" /> });
      return;
    }
    nav({ to: "/profile" });
  };

  if (isDesktopScreen) {
    return (
      <div className="bg-nav-primary z-10 mr-0 flex h-screen w-[min] shrink-0 flex-col items-center px-1.5 py-6">
        <Link to="/">
          <LogoButton className="mb-4 size-[3.2rem]" />
        </Link>

        {/* Top Button Group*/}
        <div className="mt-5 flex flex-col space-y-4">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex w-full justify-center transition-transform duration-200 ease-in-out hover:scale-110 focus:outline-none active:scale-90">
              <Tooltip>
                <TooltipTrigger asChild className="">
                  <PlusCircleIcon className="text-tx-tertiary size-8 " />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Studio</p>
                </TooltipContent>
              </Tooltip>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem className="flex items-center space-x-2 px-4 py-2" asChild>
                <Link to="/studio/create">
                  <UserPlusIcon className="size-5" />
                  <span>New Character</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem className="flex items-center space-x-2 px-4 py-2" asChild>
                <Link to="/studio/import">
                  <ArrowDownTrayIcon className="size-5" />
                  <span>Import Character</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center space-x-2 px-4 py-2"
                asChild
                onSelect={() => {
                  if (!session) {
                    createModal({ content: <AuthModal formKind="signup" /> });
                    return;
                  }
                  createModal({ content: <CreateGroupChatModal /> });
                }}
              >
                <div className="flex">
                  <UserGroupIcon className="size-5" />
                  <span>Create Group Chat</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DesktopNavButton
            icon={<ChatBubbleOvalLeftIcon className="text-tx-tertiary size-8" />}
            tooltip="Chat"
            onClick={() => handleProtectedRoute("/chat")}
          />
        </div>

        {/* Spacer */}
        <div className="grow"></div>
        <div className="border-sidebar-border mb-2 w-fit border-t px-6"></div>

        {/* Bottom Button Group*/}
        <div className="flex flex-col space-y-2">
          <DesktopNavButton
            icon={<Cog8ToothIcon className="fill-action-secondary size-8" />}
            onClick={() => handleProtectedRoute("/settings/profile")}
            tooltip="Settings"
          />
          <DesktopNavButton
            icon={<UserCircleIcon className="fill-action-secondary size-8" />}
            onClick={handleProfileRoute}
            tooltip="Profile"
          />
        </div>
      </div>
    );
  }

  const navItems = [
    { path: "/", Outline: HomeIconOutline, Solid: HomeIconSolid },
    { path: "/recent-chats", Outline: ChatIconOutline, Solid: ChatIconSolid }
  ];

  // Mobile
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-20  w-screen bg-black/80 px-8 backdrop-blur-xl">
      <div className="flex items-center justify-between">
        {navItems.map((i) => (
          <Link
            key={i.path}
            to={i.path}
            className="rounded-full px-6 py-4"
            onClick={(e) => {
              // If user clicked home again on mobile while already on home page, scroll to top
              if (location.pathname === "/" && i.path === "/" && !isDesktopScreen) {
                e.preventDefault();
                window.scrollTo({
                  top: 0,
                  left: 0,
                  behavior: "smooth"
                });
              }
            }}
          >
            <motion.div
              initial={true}
              animate={{ scale: location.pathname === i.path ? 1.1 : 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 10, duration: 100, delay: 0 }}
            >
              {location.pathname === i.path ? (
                <i.Solid className="text-tx-primary h-6 w-6" />
              ) : (
                <i.Outline className="text-action-secondary h-6 w-6" />
              )}
            </motion.div>
          </Link>
        ))}

        <motion.button
          onClick={() => {
            const actions = [
              {
                label: "New Character",
                icon: PlusCircleIcon,
                onClick: () => {
                  nav({ to: "/studio/create" });
                }
              },
              {
                label: "Import Character",
                icon: InboxArrowDownIcon,
                onClick: () => {
                  nav({ to: "/studio/import" });
                }
              },
              {
                label: "Create Group Chat",
                icon: UserGroupIcon,
                onClick: () => {
                  if (!session) {
                    createModal({ content: <AuthModal formKind="signup" /> });
                    return;
                  }
                  createModal({ content: <CreateGroupChatModal />, headerDragOnly: true });
                }
              }
            ];
            createModal({
              content: <OptionsModal actions={actions} />
            });
          }}
          className="rounded-full p-4 px-6"
          initial={true}
          animate={{ scale: location.pathname === "/studio/create" ? 1.2 : 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 20, duration: 100, delay: 0 }}
        >
          {location.pathname === "/studio/create" ? (
            <PlusIconSolid className="text-tx-primary h-6 w-6" />
          ) : (
            <PlusIconOutline className="text-action-secondary h-6 w-6" />
          )}
        </motion.button>

        {/* Only display profile button if user is logged in */}
        <motion.button
          onClick={() => {
            if (!session) {
              createModal({ content: <AuthModal formKind="signup" /> });
              return;
            }

            const actions = [
              {
                label: "My Profile",
                icon: UserIconSolid,
                onClick: () => {
                  nav({ to: "/profile" });
                }
              },
              {
                label: "My Persona",
                icon: PencilIcon,
                onClick: () => {
                  nav({ to: "/settings/persona" });
                }
              },
              {
                label: "Account",
                icon: FaAddressCard,
                onClick: () => {
                  nav({ to: "/settings/account" });
                }
              },
              {
                label: "Preferences",
                icon: Cog8ToothIcon,
                onClick: () => {
                  nav({ to: "/settings/preference" });
                }
              }
            ];
            createModal({
              content: <OptionsModal actions={actions} />
            });
          }}
          className="rounded-full p-4 px-6"
          initial={true}
          animate={{ scale: location.pathname === "/profile" ? 1.2 : 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 20, duration: 100, delay: 0 }}
        >
          {location.pathname === "/profile" ? (
            <UserIconSolid className="text-tx-primary h-6 w-6" />
          ) : (
            <UserIconOutline className="text-action-secondary h-6 w-6" />
          )}
        </motion.button>
      </div>
    </nav>
  );
}

interface DesktopNavButtonProps {
  icon: React.ReactNode;
  onClick?: () => void;
  search?: Record<any, any>;
  tooltip: string;
}

function DesktopNavButton({ icon, onClick, search, tooltip }: DesktopNavButtonProps) {
  const buttonClasses =
    "mx-auto size-16 transition-transform duration-200 ease-in-out hover:scale-110 active:scale-90 w-full";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button className={buttonClasses} onClick={onClick}>
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}
